/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { Icon } from '@mayoral/ui/index';
import { Divider, Stack, styled } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { FC, useMemo } from 'react';

import SidebarLink from './SidebarLink';

import { NavigationSection } from '@/types/app-config';

const SidebarSectionIcon = styled(Stack, {
  name: 'SidebarSection',
  slot: 'Icon',
})(({ theme }) => {
  return {
    width: 20,
    height: 20,
    minWidth: 20,
    minHeight: 20,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius * 3,
  };
});

const SidebarSectionColumn = styled(Stack, {
  name: 'SidebarSection',
  slot: 'Column',
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ isSelected }) => ({
  position: 'absolute',
  top: 0,
  left: 'calc(100% + 10px)',
  width: '100%',
  visibility: 'hidden',
  ...(isSelected && {
    visibility: 'visible',
  }),
}));

/**
 * Sidebar Nested Section
 */
interface SidebarSectionProps {
  section: NavigationSection;
  selected: NavigationSection;
  setSelected: (section: NavigationSection) => void;
}

const SidebarSection: FC<SidebarSectionProps> = ({ section, selected, setSelected }) => {
  const { t } = useTranslation();
  const isSelected = useMemo(() => section.id === selected?.id, [section, selected]);

  const handleClick = () => setSelected(section);
  const handleBack = () => setSelected(null);

  return (
    <Stack direction="column" sx={{ '&:hover .SidebarSection-Icon': { visibility: 'visible' } }}>
      {section.label && (
        <SidebarLink
          label={section.label}
          onClick={handleClick}
          startAdornment={<Icon name={section.icon} />}
          endAdornment={
            <SidebarSectionIcon
              alignItems="center"
              justifyContent="center"
              className="SidebarSection-Icon"
              sx={{
                visibility: 'hidden',
              }}
            >
              <Icon name="ArrowRightXS" viewBox="0 0 8 8" sx={{ fontSize: 8 }} />
            </SidebarSectionIcon>
          }
        />
      )}

      <SidebarSectionColumn direction="column" spacing={1} isSelected={isSelected}>
        <SidebarLink
          label={t('VOLVER_ATRAS')}
          onClick={handleBack}
          startAdornment={
            <SidebarSectionIcon
              alignItems="center"
              justifyContent="center"
              className="SidebarSection-Icon"
            >
              <Icon name="ArrowLeftXS" viewBox="0 0 8 8" sx={{ fontSize: 8 }} />
            </SidebarSectionIcon>
          }
        />

        <Divider />

        <Stack direction="column" spacing={0.5}>
          {section.items.map((item) => (
            <SidebarLink
              key={item.id}
              href={item.href}
              label={item.label}
              startAdornment={<Icon name={item.icon} />}
            />
          ))}
        </Stack>
      </SidebarSectionColumn>
    </Stack>
  );
};

export default SidebarSection;
